import { PageNumberReasonChange, PdfFileSource } from '@/hooks/viewers/usePdfViewer.ts';

/**
 * Common props shared across all viewer variance components
 */
export interface ViewerCommonProps {
	title?: string;
	fileUrl: string;
	maxWidth?: number;
	showExternalLink?: boolean;
	numPages: number;
	pageNumber: number;
	loading: boolean;
	pdfOptions: PdfFileSource;
	onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
	onDocumentLoadError: (error: Error) => void;
	goToPrevPage: () => void;
	goToNextPage: () => void;
	handlePageNumberChange: (page: number, reason?: PageNumberReasonChange) => void;
	openInNewTab: () => void;
	setPageChangeReason: (callback: (reason: PageNumberReasonChange) => void) => void;
	onExpand: () => void;
	disableHoverEffects?: boolean;
	isHighlighted?: boolean;
	showExpandButton?: boolean;
	showPageControls?: boolean;
	showPreviewHeader?: boolean;
	unstyledCompact?: boolean;
	onPreviewAspectChange?: (aspect: number) => void;
}

/**
 * Props specific to compact mode PDF viewer
 */
export type ViewerCompactViewerProps = Pick<
	ViewerCommonProps,
	| 'title'
	| 'maxWidth'
	| 'showExternalLink'
	| 'numPages'
	| 'pageNumber'
	| 'loading'
	| 'pdfOptions'
	| 'onDocumentLoadSuccess'
	| 'onDocumentLoadError'
	| 'goToPrevPage'
	| 'goToNextPage'
	| 'openInNewTab'
	| 'onExpand'
	| 'disableHoverEffects'
	| 'isHighlighted'
	| 'showExpandButton'
	| 'showPageControls'
	| 'showPreviewHeader'
	| 'unstyledCompact'
	| 'onPreviewAspectChange'
>;

/**
 * Props specific to full mode PDF viewer
 */
export type ViewerFullViewerProps = Pick<
	ViewerCommonProps,
	| 'title'
	| 'numPages'
	| 'pageNumber'
	| 'loading'
	| 'pdfOptions'
	| 'onDocumentLoadSuccess'
	| 'onDocumentLoadError'
	| 'goToPrevPage'
	| 'goToNextPage'
	| 'onExpand'
	| 'showPageControls'
>;

/**
 * Common hover transition styles used across PDF viewer components
 */
export const commonHoverTransition = 'opacity 0.2s ease-in-out';

/**
 * Common group hover opacity styles
 */
export const groupHoverOpacity = {
	opacity: 0,
	_groupHover: { opacity: 1 },
	transition: commonHoverTransition,
};

/**
 * Background tint applied to a compact viewer when it is the highlighted (cited) source.
 */
export const COMPACT_VIEWER_HIGHLIGHT_BG = 'yellow.100';
